// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { HceFileManagementService } from '../../hce-file-management/hce-file-management.service';
import { Observable, of, switchMap, throwError } from 'rxjs';
import { FileMeta, HceApiService } from '../../hce-api/hce-api.service';
import { Analysis } from '../../../../interfaces/analysis';

@Injectable({ providedIn: 'root' })
export class HceAnalysisMetadataService {

  // ===== Constructor & DI =====
  constructor(
    private readonly api: HceApiService,
    private readonly file: HceFileManagementService,
  ) {}

  // ===== Public API: Metadata Retrieval =====

  // -- Assembly (asm)
  public getAsmUnusedInitMaps2dHistFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'asm_results/output/unused_init_maps_2d_hist.tsv');
  }
  public getAsmUnusedMaps2dHistFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'asm_results/output/unused_maps_2d_hist.tsv');
  }
  public getAsmUsedInitMaps2dHistFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'asm_results/output/used_init_maps_2d_hist.tsv');
  }
  public getAsmUsedMaps2dHistFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'asm_results/output/used_maps_2d_hist.tsv');
  }
  public getAsmNabsysFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'asm_results/output/assembly/nabsys.zip');
  }
  public getAsmContigFileMeta(analysisId: number, contigId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, `asm_results/output/assembly/gajson/contigs/${contigId}.json.gz`);
  }
  public getAsmContigFilesMeta(analysis: Analysis): Observable<FileMeta[]> {
    return this.getFilesMeta(analysis, 'asm_results/output/assembly/gajson/contigs');
  }
  public getAsmParamsFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'asm_results/output/config/assembly_params.json');
  }

  // -- Input
  public getInputFilesMeta(analysis: Analysis): Observable<FileMeta[]> {
    return this.getFilesMeta(analysis, 'input');
  }
  public getInputContigsFilesMeta(analysis: Analysis): Observable<FileMeta[]> {
    return this.getFilesMeta(analysis, 'input/contigs');
  }
  public getInputReadsFilesMeta(analysis: Analysis): Observable<FileMeta[]> {
    return this.getFilesMeta(analysis, 'input/reads');
  }

  // -- Map2Ref
  public getMap2refGenderPredictionFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'map2ref/gender_prediction.json');
  }
  public getMap2refGappedAlignerParamsFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'map2ref/config/gapped_aligner_params.json');
  }

  // -- QC
  public getQcStats0FileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'qc_results/stats0_results.json');
  }

  // -- SV
  public getSvAlignedAsmStatsFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'sv/results/aligned_assembly_stats.json');
  }
  public getSvEnhancedCoverageFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'sv/results/enhanced.coverage.txt');
  }
  public getSvEnhancedHsvFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'sv/results/enhanced.hsv.json');
  }
  public getSvEnhancedMultihitsFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'sv/results/enhanced.multihits.json');
  }
  public getSvEnhancedRefGeneFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'sv/results/enhanced.ref_gene.txt');
  }
  public getSvDetectionConfigFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'sv/results/sv_detection_config.json');
  }
  public getSvGappedAlignerParamsFileMeta(analysisId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, 'sv/results/config/gapped_aligner_params.json');
  }
  public getSvEnhancedContigsFileMeta(analysisId: number, chromosomeId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, `sv/results/enhanced.contigs/${chromosomeId}.json`);
  }
  public getSvEnhancedContigsFilesMeta(analysis: Analysis, pattern?: RegExp): Observable<FileMeta[]> {
    return this.getSvEnhancedChrFilesMeta(analysis, 'contigs', pattern);
  }
  public getSvEnhancedGappedAlnsFileMeta(analysisId: number, chromosomeId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, `sv/results/enhanced.gapped_alns/${chromosomeId}.json`);
  }
  public getSvEnhancedGappedAlnsFilesMeta(analysis: Analysis, pattern?: RegExp): Observable<FileMeta[]> {
    return this.getSvEnhancedChrFilesMeta(analysis, 'gapped_alns', pattern);
  }
  public getSvEnhancedScaledFileMeta(analysisId: number, chromosomeId: number): Observable<FileMeta> {
    return this.getFileMeta(analysisId, `sv/results/enhanced.scaled/${chromosomeId}.json`);
  }
  public getSvEnhancedScaledFilesMeta(analysis: Analysis, pattern?: RegExp): Observable<FileMeta[]> {
    return this.getSvEnhancedChrFilesMeta(analysis, 'scaled', pattern);
  }

  // ===== Private helpers: Metadata helpers =====
  private getFileMeta(analysisId: number, filename: string): Observable<FileMeta> {
    return this.file.getAnalysisSignedUrl(analysisId, filename).pipe(
      switchMap(url => this.api.getFileMeta(url))
    );
  }

  private getFilesMeta(analysis: Analysis, folderName: string): Observable<FileMeta[]> {
    const relativePath = `${analysis.FolderName}/Results/Reports/${folderName}`;
    return this.file.getFilesMeta(relativePath);
  }

  private getSvEnhancedChrFilesMeta(
    analysis: Analysis,
    type: 'contigs' | 'gapped_alns' | 'scaled',
    pattern?: RegExp,
  ): Observable<FileMeta[]> {
    const folderPath = `${analysis.FolderName}/Results/Reports/sv/results/enhanced.${type}`;
    return this.file.existsFolder(folderPath).pipe(
      switchMap(exists => {
        if (!exists) {
          const message = `SV enhanced ${type} folder does not exist: ${folderPath}`;
          return throwError(() => new Error(message));
        }
        return this.file.getFilesMeta(folderPath, pattern);
      })
    );
  }
}
